import { ActivatedRoute, Router } from '@angular/router';
import { Component, ChangeDetectionStrategy, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { ProductsService } from './services/products.service';
import { WindowService } from '@shared/window/window.service';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { Product, ProductCategory } from '@shared/models';
import { ProductsRowType1Component } from './shared/products-row-type-1/products-row-type-1.component';
import { ProductsRowType2Component } from './shared/products-row-type-2/products-row-type-2.component';
import { ProductThumbnailComponent } from '../../shared/product-thumbnail/product-thumbnail.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-products-listing',
    templateUrl: './products-listing.component.html',
    styleUrls: ['./products-listing.component.scss'],
    providers: [ProductsService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ProductsRowType1Component, ProductsRowType2Component, ProductThumbnailComponent, PaginationComponent, AsyncPipe, TranslatePipe],
})
export class ProductsListingComponent implements OnInit, OnDestroy {
    private productsService = inject(ProductsService);
    private windowService = inject(WindowService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);

    @Input() set category(value) {
        this.category$.next(value);
    }

    get category(): ProductCategory | null {
        return this.category$.value;
    }

    category$ = new BehaviorSubject<ProductCategory | null>(null);
    totals$ = this.productsService.totals$;
    products$ = this.productsService.products$;
    searching$ = this.productsService.searching$;
    error$ = this.productsService.error$;

    pageNumber$ = new Subject<number>();
    isSearch$ = new BehaviorSubject(false);

    productsRows$ = this.products$.pipe(
        filter((products) => products !== null),
        map((products) => {
            return [...products];
        }),
        map((products) => {
            const rows: Product[][] = [];
            let count = products.length;
            while (count > 0) {
                if (rows.length % 2 === 0) {
                    count = count - 15;
                    rows.push([...products.splice(0, 15)]);
                } else {
                    count = count - 7;
                    rows.push([...products.splice(0, 7)]);
                }
            }
            return rows;
        }),
    );

    searchQuery$ = this.activatedRoute.queryParams.pipe(map((params) => (params['q'] ? params['q'] : '')));

    private subscriptions$: Subscription = new Subscription();

    ngOnInit(): void {
        this.subscriptions$.add(
            this.totals$
                .pipe(
                    filter((data) => data.total !== null),
                    tap((data) => {
                        if (data.start && data.total && data.start > data.total) {
                            void this.router.navigate(['..', '1'], {
                                relativeTo: this.activatedRoute,
                                queryParamsHandling: 'merge',
                            });
                        }
                    }),
                )
                .subscribe(),
        );

        this.subscriptions$.add(
            combineLatest([
                this.pageNumber$.pipe(distinctUntilChanged()),
                this.activatedRoute.queryParams.pipe(map((params) => (params['q'] ? params['q'] : ''))),
                this.category$,
            ])
                .pipe(
                    tap(([number, query, category]) => {
                        this.isSearch$.next(query ? true : false);
                        this.productsService.dispatch(number, category?.slug ?? null, query);
                    }),
                )
                .subscribe(),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions$.unsubscribe();
    }

    get limit(): number {
        return this.productsService.limit;
    }

    onPageChange(pageNumber: number): void {
        this.windowService.scrollTop();
        this.pageNumber$.next(pageNumber);
    }
}
